import { usePatchMe } from '@/hooks/usePatching'
import {
  CONNECTION_DELETE_BLOCKED_REASON,
  CONNECTION_READ_ONLY_NOTE,
  CONNECTION_SAVE_BLOCKED_REASON,
  canDeleteConnection,
  canEditConnection,
} from '@/utils/connectionPermissions'

export interface ConnectionPermissions {
  /** An existing connection is open and this user may not change it: its fields are read-only. */
  readOnly: boolean
  /** Save configuration works: always for a new connection, and for an existing one only for admins. */
  canSave: boolean
  canDelete: boolean
  /** Why the field, Save and Delete are locked, or null when they are not. */
  readOnlyNote: string | null
  saveBlockedReason: string | null
  deleteBlockedReason: string | null
}

/**
 * What the signed-in user may do with the connection on screen. `existing` is true once a saved connection is open; a new
 * one being typed in is always editable, and becomes an existing one (read-only for a non-admin) the moment it is saved.
 * While the user's role is still loading, or could not be loaded, an existing connection stays locked.
 */
export function useConnectionPermissions(existing: boolean): ConnectionPermissions {
  const { data: me } = usePatchMe()
  const mayEdit = canEditConnection(me)
  const mayDelete = canDeleteConnection(me)
  const readOnly = existing && !mayEdit
  return {
    readOnly,
    canSave: !existing || mayEdit,
    canDelete: mayDelete,
    readOnlyNote: readOnly ? CONNECTION_READ_ONLY_NOTE : null,
    saveBlockedReason: readOnly ? CONNECTION_SAVE_BLOCKED_REASON : null,
    deleteBlockedReason: existing && !mayDelete ? CONNECTION_DELETE_BLOCKED_REASON : null,
  }
}
